'use client';

import type { ProfileModuleDto } from '@up2stay/up2promo-api-client';
import { Mail, Phone } from 'lucide-react';

import { formatPhoneForDisplay } from '../../components/format-phone';
import { cn } from '../../utils/cn';
import { useProfileLayout } from '../layouts/profile-layout-context';
import {
  ModuleEntryCard,
  ModuleEntryIcon,
  ModuleEntryLabelValue,
  ModuleEntryRow,
} from './module-entry-card';

interface ContactInfoModuleDisplayProps {
  module: ProfileModuleDto;
}

type ContactEntry = {
  id?: string;
  kind: 'email' | 'phone';
  label?: string;
  value: string;
  type?: 'private' | 'business';
  visible?: boolean;
};

function isPrivateSoundingLabel(label: string | undefined): boolean {
  const normalized = label?.trim().toLowerCase() ?? '';
  return (
    normalized === 'privat' ||
    normalized === 'private' ||
    normalized === 'persönlich' ||
    normalized === 'persoenlich' ||
    normalized === 'personal'
  );
}

function getContactItems(rawConfig: Record<string, unknown>): ContactEntry[] {
  const items = (rawConfig.items as ContactEntry[] | undefined) ?? [];
  return items.filter(
    (entry) => entry.visible !== false && (entry.value ?? '').trim(),
  );
}

/** Displays contact information as cards (one per entry). */
export function ContactInfoModuleDisplay({
  module,
}: ContactInfoModuleDisplayProps) {
  const layout = useProfileLayout();
  const entries = getContactItems(module.config || {});

  if (entries.length === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full',
        // Classic = hairline list (no inter-row gap). Cards/centered = 10px stack.
        layout === 'classic' ? 'flex flex-col' : 'flex flex-col gap-2.5',
      )}
    >
      {entries.map((entry, index) => {
        const isEmail = entry.kind === 'email';
        const href = isEmail ? `mailto:${entry.value}` : `tel:${entry.value}`;
        const Icon = isEmail ? Mail : Phone;
        // Centered handover pills: left = kind label (E-Mail/Telefon), right = value.
        // Fall back to kind when the entry has no public label.
        const publicLabel =
          entry.label?.trim() &&
          entry.type !== 'private' &&
          !isPrivateSoundingLabel(entry.label)
            ? entry.label.trim()
            : undefined;
        const centeredLabel = publicLabel ?? (isEmail ? 'E-Mail' : 'Telefon');
        const label =
          layout === 'centered'
            ? centeredLabel
            : entry.type === 'private' || isPrivateSoundingLabel(entry.label)
              ? undefined
              : entry.label;

        return (
          <ModuleEntryCard
            key={entry.id ?? `${entry.kind}-${index}-${entry.value}`}
          >
            <a href={href} className="block transition-colors">
              <ModuleEntryRow>
                <ModuleEntryIcon>
                  <Icon strokeWidth={1.7} />
                </ModuleEntryIcon>
                <ModuleEntryLabelValue
                  label={label}
                  value={
                    isEmail ? entry.value : formatPhoneForDisplay(entry.value)
                  }
                />
              </ModuleEntryRow>
            </a>
          </ModuleEntryCard>
        );
      })}
    </div>
  );
}
